import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, IconButton, Typography, Box, Chip,
  FormControlLabel, Switch, MenuItem,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import axios from 'axios';

/**
 * EditProjectModal — edit project name, buildings/areas after creation.
 *
 * Props:
 *   open, onClose, project, url, onSaved
 */
const EditProjectModal = ({ open, onClose, project, url, onSaved }) => {
  const [name, setName] = useState('');
  const [buildings, setBuildings] = useState([]);
  const [newBuilding, setNewBuilding] = useState('');
  const [conditionModifiers, setConditionModifiers] = useState([]);
  const [soilPreset, setSoilPreset] = useState('normal');
  const [saving, setSaving] = useState(false);

  const deriveSoilPreset = (mods) => {
    const rocky = mods.find(m => m.name === 'Rocky/Caliche Soil');
    if (!rocky || !rocky.enabled) return 'normal';
    if (rocky.factor === -0.10) return 'mixed';
    return 'rocky';
  };

  useEffect(() => {
    if (open && project) {
      setName(project.name || '');
      setBuildings((project.buildings || []).map(b => typeof b === 'string' ? { name: b } : b));
      setNewBuilding('');
      const mods = (project.conditionModifiers || []).map(m => ({ ...m }));
      setConditionModifiers(mods);
      setSoilPreset(deriveSoilPreset(mods));
    }
  }, [open, project]);

  const addBuilding = () => {
    const trimmed = newBuilding.trim();
    if (!trimmed || buildings.find(b => b.name === trimmed)) return;
    setBuildings([...buildings, { name: trimmed }]);
    setNewBuilding('');
  };

  const removeBuilding = (idx) => {
    setBuildings(buildings.filter((_, i) => i !== idx));
  };

  const toggleModifier = (idx) => {
    const updated = conditionModifiers.map((m, i) => i === idx ? { ...m, enabled: !m.enabled } : m);
    setConditionModifiers(updated);
    // Keep soil preset in sync when Rocky/Caliche toggle changes
    if (conditionModifiers[idx]?.name === 'Rocky/Caliche Soil') {
      setSoilPreset(updated[idx].enabled ? (updated[idx].factor === -0.10 ? 'mixed' : 'rocky') : 'normal');
    }
  };

  const handleSoilPreset = (preset) => {
    setSoilPreset(preset);
    setConditionModifiers(prev => prev.map(m => {
      if (m.name !== 'Rocky/Caliche Soil') return m;
      if (preset === 'normal') return { ...m, enabled: false, factor: -0.20 };
      if (preset === 'mixed') return { ...m, enabled: true, factor: -0.10 };
      return { ...m, enabled: true, factor: -0.20 }; // rocky
    }));
  };

  const handleSave = async () => {
    if (!url || !project?._id) return;
    setSaving(true);
    try {
      await axios.put(`${url}/api/bid-estimation/projects/${project._id}`, {
        name,
        buildings,
        conditionModifiers,
      });
      // Re-hydrate all service costs when conditions changed
      const conditionsChanged = JSON.stringify(conditionModifiers) !== JSON.stringify(project.conditionModifiers || []);
      if (conditionsChanged) {
        await axios.post(`${url}/api/bid-estimation/projects/${project._id}/project-services/recalculate-all`).catch(() => {});
      }
      if (onSaved) onSaved({ ...project, name, buildings, conditionModifiers });
      onClose();
    } catch (err) {
      console.error('Failed to update project:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <span>Edit Project</span>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <TextField
          label="Project Name"
          fullWidth
          variant="outlined"
          size="small"
          value={name}
          onChange={(e) => setName(e.target.value)}
          style={{ marginBottom: 20 }}
        />

        <Typography style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: '#555' }}>
          Buildings / Areas
        </Typography>
        <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginBottom: 12 }}>
          Used to group line items, plan takeoff placements, and cost breakdowns by area.
        </Typography>

        <Box display="flex" flexWrap="wrap" style={{ gap: 6, marginBottom: 12 }}>
          {buildings.map((b, idx) => (
            <Chip
              key={idx}
              label={b.name}
              onDelete={() => removeBuilding(idx)}
              deleteIcon={<DeleteIcon style={{ fontSize: 14 }} />}
              variant="outlined"
              size="small"
            />
          ))}
          {buildings.length === 0 && (
            <Typography variant="caption" color="textSecondary">No areas defined</Typography>
          )}
        </Box>

        <Box display="flex" style={{ gap: 8 }}>
          <TextField
            size="small"
            variant="outlined"
            placeholder="Add area name..."
            value={newBuilding}
            onChange={(e) => setNewBuilding(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') addBuilding(); }}
            style={{ flex: 1 }}
          />
          <Button
            variant="outlined"
            size="small"
            onClick={addBuilding}
            startIcon={<AddIcon />}
            style={{ textTransform: 'none' }}
            disabled={!newBuilding.trim()}
          >
            Add
          </Button>
        </Box>

        {conditionModifiers.length > 0 && (
          <>
            <Typography style={{ fontSize: 13, fontWeight: 600, marginTop: 24, marginBottom: 8, color: '#555' }}>
              Site Conditions
            </Typography>
            <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginBottom: 12 }}>
              Adjust production rates for site-specific challenges. Enabled conditions slow down work, increasing labor/equipment hours.
            </Typography>

            <TextField
              select
              label="Soil Condition"
              value={soilPreset}
              onChange={(e) => handleSoilPreset(e.target.value)}
              variant="outlined"
              size="small"
              fullWidth
              style={{ marginBottom: 16 }}
            >
              <MenuItem value="normal">Normal Desert Soil</MenuItem>
              <MenuItem value="rocky">Rocky / Caliche (-20%)</MenuItem>
              <MenuItem value="mixed">Mixed (partial caliche) (-10%)</MenuItem>
            </TextField>

            {conditionModifiers.map((m, idx) => (
              <Box key={idx} display="flex" alignItems="center" justifyContent="space-between" style={{ marginBottom: 4 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!m.enabled}
                      onChange={() => toggleModifier(idx)}
                      size="small"
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      {m.name}: {Math.round(m.factor * 100)}%
                    </Typography>
                  }
                />
              </Box>
            ))}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saving || !name.trim()}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditProjectModal;
